import React from 'react';
import { ErrorMessage } from 'formik';
import { Typography, Radio, Select, Space } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { colors } from '../constants';
import {
  StepTitle, FormSection, FormItem, SectionTitle, SectionDescription,
  ErrorText, InfoBox, InputWrapper, LargeSelect, LargeRadio
} from '../styledComponents';

const { Paragraph } = Typography;
const { Option } = Select;
const RadioGroup = Radio.Group;

const StepDemographics = ({ values, errors, touched, setFieldValue }) => (
  <>
    <StepTitle level={4}>Demographics</StepTitle>
    
    <InfoBox>
      <div className="icon"><InfoCircleOutlined /></div>
      <div className="content">
        <Paragraph style={{ margin: 0, color: colors.text }}>
          This information helps us better understand the communities we serve and improve our healthcare services.
        </Paragraph>
      </div>
    </InfoBox>
    
    <FormSection>
      <FormItem>
        <SectionTitle>Birth Gender</SectionTitle>
        <InputWrapper>
          <RadioGroup
            value={values.birthGender}
            onChange={(e) => setFieldValue('birthGender', e.target.value)}
          >
            <Space direction="horizontal" size="large" wrap>
              <LargeRadio value="male">Male</LargeRadio>
              <LargeRadio value="female">Female</LargeRadio>
            </Space>
          </RadioGroup>
        </InputWrapper>
        <ErrorMessage name="birthGender" component={ErrorText} />
      </FormItem>
      
      <FormItem>
        <SectionTitle>Race/Ethnicity</SectionTitle>
        <InputWrapper>
          <LargeSelect
            style={{ 
              width: '100%',
              borderColor: touched.raceEthnicity && errors.raceEthnicity ? colors.error : null 
            }}
            placeholder="Select Race/Ethnicity"
            value={values.raceEthnicity}
            onChange={(value) => setFieldValue('raceEthnicity', value)}
          >
            <Option value="blackOrAfricanAmerican">Black or African American</Option>
            <Option value="white">White</Option>
            <Option value="hispanicOrLatino">Hispanic or Latino</Option>
            <Option value="asianOrAsianAmerican">Asian or Asian American</Option>
            <Option value="americanIndianOrAlaskaNative">American Indian or Alaska Native</Option>
            <Option value="nativeHawaiianOrOPI">Native Hawaiian or OPI</Option>
            <Option value="other">Other</Option>
          </LargeSelect>
        </InputWrapper>
        <ErrorMessage name="raceEthnicity" component={ErrorText} />
      </FormItem>
    </FormSection>
    
    <FormSection>
      <FormItem>
        <SectionTitle>Annual Household Income Range</SectionTitle>
        <SectionDescription>
          This information helps us understand the economic diversity of our community and ensure accessible healthcare.
        </SectionDescription>
        <InputWrapper>
          <RadioGroup
            value={values.householdIncome}
            onChange={(e) => setFieldValue('householdIncome', e.target.value)}
          >
            <Space direction="vertical" size="small">
              <LargeRadio value="0-26700">$0-$26,700</LargeRadio>
              <LargeRadio value="26701-44500">$26,701-$44,500</LargeRadio>
              <LargeRadio value="44501-71200">$44,501-$71,200</LargeRadio>
              <LargeRadio value="over71200">Over $71,200</LargeRadio>
              <LargeRadio value="preferNotToAnswer">Prefer not to answer</LargeRadio>
            </Space>
          </RadioGroup>
        </InputWrapper>
        <ErrorMessage name="householdIncome" component={ErrorText} />
      </FormItem>
    </FormSection>
    
    <FormSection>
      <FormItem>
        <SectionTitle>Insurance Status</SectionTitle>
        <SectionDescription>
          Understanding your insurance coverage helps us coordinate your care and test processing effectively.
        </SectionDescription>
        <InputWrapper>
          <LargeSelect
            style={{ 
              width: '100%',
              borderColor: touched.insuranceStatus && errors.insuranceStatus ? colors.error : null 
            }}
            placeholder="Select Insurance Status"
            value={values.insuranceStatus}
            onChange={(value) => setFieldValue('insuranceStatus', value)}
          >
            <Option value="private">Private Insurance</Option>
            <Option value="employerInsured">Insured through Employer</Option>
            <Option value="medicare">Medicare</Option>
            <Option value="medicaid">Medicaid</Option>
            <Option value="tricare">Tricare/Military</Option>
            <Option value="military">Military</Option>
            <Option value="uninsured">Uninsured</Option>
            <Option value="noCoverage">No Coverage</Option>
            <Option value="other">Other</Option>
          </LargeSelect>
        </InputWrapper>
        <ErrorMessage name="insuranceStatus" component={ErrorText} />
      </FormItem>
    </FormSection>
  </>
);

export default StepDemographics;
